// src/services/leave.service.js
import { api } from './api';

class LeaveService {
    /**
     * Get leave balance for current employee
     */
    async getLeaveBalance() {
        try {
            const response = await api.get('/api/leaves/balance');
            return response.data;
        } catch (error) {
            console.error('Error fetching leave balance:', error);
            throw error;
        }
    }

    /**
     * Get monthly leave summary for current month
     */
    async getMonthlyLeaveSummary() {
        try {
            const now = new Date();
            const month = now.getMonth() + 1;
            const year = now.getFullYear();
            const response = await api.get('/api/leaves/monthly-summary', {
                params: { month, year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching monthly leave summary:', error);
            return {
                cl: { availed: 0, total: 0 },
                sl: { availed: 0, total: 0 },
                lop: 0
            };
        }
    }

    /**
     * Get leave history for current employee
     */
    async getLeaveHistory(params = {}) {
        try {
            console.log('📡 Calling API: GET /api/leaves/my-leaves', { params });
            const response = await api.get('/api/leaves/my-leaves', { params });
            console.log('🔍 Raw API response for my-leaves:', response);
            console.log('🔍 Response data:', response.data);
            console.log('🔍 Response data.leaves:', response.data.leaves);
            console.log('🔍 Response data.requests:', response.data.requests);

            // The backend may return either 'leaves' or 'requests' depending on the endpoint
            const result = response.data.leaves || response.data.requests || [];
            console.log('✅ Returning leave history:', result.length, 'leaves', result);
            return result;
        } catch (error) {
            console.error('❌ Error fetching leave history:', error);
            console.error('❌ Error details:', error.response?.data);
            console.error('❌ Error status:', error.response?.status);
            console.error('❌ Returning empty array due to error');
            return [];
        }
    }

    /**
     * Apply for leave
     */
    async applyLeave(leaveData) {
        try {
            const response = await api.post('/api/leaves/request', leaveData);
            return response.data;
        } catch (error) {
            console.error('Error applying leave:', error);
            throw error;
        }
    }

    /**
     * Cancel leave
     */
    async cancelLeave(leaveId) {
        try {
            const response = await api.post(`/api/leaves/cancel/${leaveId}`);
            return response.data;
        } catch (error) {
            console.error('Error cancelling leave:', error);
            throw error;
        }
    }

    /**
     * Get team leave requests (for managers)
     */
    async getTeamLeaves() {
        try {
            const response = await api.get('/api/leaves/team');
            return response.data.leaves || [];
        } catch (error) {
            console.error('Error fetching team leaves:', error);
            return [];
        }
    }

    /**
     * Approve leave (for managers)
     */
    async approveLeave(leaveId, remarks = '') {
        try {
            const response = await api.post(`/api/leaves/approve/${leaveId}`, {
                remarks
            });
            return response.data;
        } catch (error) {
            console.error('Error approving leave:', error);
            throw error;
        }
    }

    /**
     * Reject leave (for managers)
     */
    async rejectLeave(leaveId, reason = '') {
        try {
            const response = await api.post(`/api/leaves/reject/${leaveId}`, {
                reason
            });
            return response.data;
        } catch (error) {
            console.error('Error rejecting leave:', error);
            throw error;
        }
    }

    /**
     * Revoke approved leave (admin only)
     * Changes an approved leave back to rejected and restores leave balance
     */
    async revokeApprovedLeave(leaveId, reason) {
        try {
            const response = await api.post(`/admin/leaves/${leaveId}/revoke`, {
                reason
            });
            return response.data;
        } catch (error) {
            console.error('Error revoking approved leave:', error);
            throw error;
        }
    }

    /**
     * Get all leave requests (for admin)
     */
    async getAllLeaveRequests() {
        try {
            const response = await api.get('/api/leaves/all-requests');
            return response.data;
        } catch (error) {
            console.error('Error fetching all leave requests:', error);
            throw error;
        }
    }

    /**
     * Update leave status (for managers/admin)
     */
    async updateLeaveStatus(leaveId, status) {
        try {
            const response = await api.put(`/api/leaves/${leaveId}/status`, {
                status
            });
            return response.data;
        } catch (error) {
            console.error('Error updating leave status:', error);
            throw error;
        }
    }

    /**
     * Award compensatory off (for managers/admin)
     */
    async awardCompensatoryOff(compOffData) {
        try {
            const response = await api.post('/api/leaves/award-comp-off', compOffData);
            return response.data;
        } catch (error) {
            console.error('Error awarding compensatory off:', error);
            throw error;
        }
    }

    /**
     * Get leave balance for a specific employee (admin only)
     */
    async getEmployeeLeaveBalance(employeeId) {
        try {
            const response = await api.get(`/api/leaves/employee/${employeeId}/balance`);
            return response.data;
        } catch (error) {
            console.error('Error fetching employee leave balance:', error);
            throw error;
        }
    }

    /**
     * Get leave history for a specific employee (admin only)
     */
    async getEmployeeLeaveHistory(employeeId) {
        try {
            const response = await api.get(`/api/leaves/employee/${employeeId}/history`);
            return response.data.leaves || response.data.requests || [];
        } catch (error) {
            console.error('Error fetching employee leave history:', error);
            return [];
        }
    }

    /**
     * Fallback: Get leave data from localStorage
     */
    getLocalStorageLeaveData() {
        // try {
        //     const stored = localStorage.getItem('hrportal_leaves');
        //     if (!stored) {
        //         return { balances: [], history: [], pending: [] };
        //     }
        //     return JSON.parse(stored);
        // } catch (error) {
        //     console.error('Error loading leave data from localStorage:', error);
        //     return { balances: [], history: [], pending: [] };
        // }
    }
}

export default new LeaveService();
